import React, { Component } from 'react'
import { connect } from 'react-redux'
import formatDate from 'dateformat'
import TimeAgo from 'timeago.js'
import difference from 'lodash.difference'

import classNames from './ScriptsInfo.css'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { fetch } from '../utils/fetch'
import { ENDPOINT_SCRIPTS_INFO } from '../constants'

const formatTimeAgo = new TimeAgo().format

const KEY_CLEANUP_S3 = 'blayzr-cleanup-s3.py'
const KEY_CREATE_ALBUMS = 'blayzr-create-albums.py'
const KEY_CREATE_CANDIDATES = 'blayzr-create-candidates.py'
const KEY_CREATE_IMAGE_VARIANTS = 'blayzr-create-image-variants.py'
const KEY_CREATE_KEYWORDS = 'blayzr-create-keywords.py'
const KEY_DATES = 'dates.py'
const KEY_EVENT_PATH = 'eventpath.py'
const KEY_BACKUP = 'backup.py'
const KEY_GRAPH_BACKUP_UPDATED = 'graph_backup__updated_at'
const KEY_GRAPH_DISCOVER_UPDATED = 'graph_discover__updated_at'
const KEY_GRAPH_DISPLAY_UPDATED = 'graph_display__updated_at'
const KEY_GRAPH_NEW_NODES = 'graph_new_nodes'
const KEY_GRAPH_OLD_NODES = 'graph_old_nodes'
const KEY_GRAPH_RESTORE = 'graph.py_restore'
const KEY_GRAPH_STRUCTURE_UPDATED = 'graph_structure__updated_at'
const KEY_GRAPH_UPDATED = 'graph_updated_date'
const KEY_GRAPH_UPLOAD = 'graph.py_upload'
const KEY_GRAPH_HIERARCHY_CREATED = 'graph_hierarchy_created_at'
const KEY_S3_TO_ELASTICSEARCH = 'blayzr-s3-to-elasticsearch.py'
const KEY_SET_ALBUM_COVERS = 'set-dynamic-albums-cover'
const KEY_SET_ALBUMPATH_COUNTS = 'set_albumpath_counts.py'
const KEY_UPLOAD_ALBUMPATHS = 'blayzr-upload-albumpaths.py'
const KEY_UPLOAD_CANDIDATES = 'blayzr-upload-candidates.py'
const KEY_UPLOAD_KEYWORD_HIERARCHY = 'blayzr-upload-keyword_hierarchy.py'

const SCRIPT_LIST = [
  {
    title: 'Data',
    scripts: [
      KEY_DATES,
      KEY_EVENT_PATH,
      KEY_BACKUP
    ]
  },
  {
    title: 'Scripts',
    scripts: [
      KEY_CREATE_CANDIDATES,
      KEY_UPLOAD_CANDIDATES,
      KEY_CLEANUP_S3,
      KEY_CREATE_IMAGE_VARIANTS,
      KEY_SET_ALBUMPATH_COUNTS,
      KEY_SET_ALBUM_COVERS,
      KEY_CREATE_ALBUMS,
      KEY_CREATE_KEYWORDS,
      KEY_S3_TO_ELASTICSEARCH,
      KEY_UPLOAD_KEYWORD_HIERARCHY,
      KEY_UPLOAD_ALBUMPATHS
    ]
  },
  {
    title: 'Graph',
    scripts: [
      KEY_GRAPH_BACKUP_UPDATED,
      KEY_GRAPH_UPLOAD,
      KEY_GRAPH_RESTORE,
      KEY_GRAPH_NEW_NODES,
      KEY_GRAPH_OLD_NODES,
      KEY_GRAPH_DISPLAY_UPDATED,
      KEY_GRAPH_DISCOVER_UPDATED,
      KEY_GRAPH_STRUCTURE_UPDATED,
      KEY_GRAPH_UPDATED,
      KEY_GRAPH_HIERARCHY_CREATED
    ]
  }
]

// Data
// dates.py
// eventpath.py
// (both red if before s3-to-elasticsearch)

// Scripts
// Create candidates
// Upload candidates (red if create is after it)
// Cleanup S3 (red if upload cand is after)
// Create image variants (red if upload cand is after it)
// set album path counts (red if before upload cands)
// set dyn album cover
// Create albums (red if upload cand is after it)
// Create keywords (red if upload cand is after it)
// s3-to-elasticsearch (red if graph upload is before) (red if upload cand is after) (red if create keywords is after or create albums is after)

// Graph
// graph_last_backup (red if old node count is zero and backup is before last graph update time)
// graph_upload (red if create keywords is after it)
// graph_new_nodes
// graph_old_nodes
// graph_visibility_update (red if before last graph update)
// graph_weighting_update (red if before last graph update)
// graph_structure_update (red if before last graph update)

class ScriptsInfo extends Component {
  state = {
    data: null,
    error: null
  }
  componentDidMount () {
    this.fetchData()
  }
  fetchData = () => {
    this.setState({
      error: null,
      fetching: true
    })
    this.props.fetchData().then(
      data => this.setState({ data, fetching: false }),
      error => this.setState({ error, fetching: false })
    )
  }
  renderList (title, scripts, data) {
    return (
      <div key={title}>
        <h2>{title}</h2>
        <table>
          <tbody>
            {scripts.map(key => this.renderScript(key, data))}
          </tbody>
        </table>
      </div>
    )
  }
  renderScript (key, data) {
    const flags = getFlags(key, data)
    const rowClass = flags.length !== 0 ? classNames.red : classNames.row
    const isDate = typeof data[key] === 'string'
    return (
      <tr key={key} className={rowClass} title={flags.join(', ') || undefined}>
        <td>{key}</td>
        <td>{isDate ? formatDate(new Date(data[key]), 'HH:MM, d mmm yyyy') : data[key]}</td>
        <td>{isDate ? formatTimeAgo(new Date(data[key])) : null}</td>
      </tr>
    )
  }
  render () {
    const { data, fetching, error } = this.state

    if (error) {
      return (
        <ErrorMessage
          message='There was an error fetching script info'
          onRetry={this.fetchData}
        />
      )
    }

    if (!data) {
      return <LoadingSpinner />
    }

    const allScripts = SCRIPT_LIST.reduce((array, list) => array.concat(list.scripts), [])
    const other = difference(Object.keys(data), allScripts)

    return (
      <div className={classNames.page}>
        {SCRIPT_LIST.map(list => this.renderList(list.title, list.scripts, data))}
        {other.length !== 0 &&
          this.renderList('Other', other, data)
        }
        <button onClick={this.fetchData} disabled={fetching}>
          {fetching ? 'Refreshing…' : 'Refresh Data'}
        </button>
      </div>
    )
  }
}

function getFlags (key, data) {
  const isEarlier = k => new Date(data[k]) < new Date(data[key])
  const isLater = k => new Date(data[k]) > new Date(data[key])
  const flags = []
  if (key === KEY_DATES || key === KEY_EVENT_PATH) {
    if (isLater(KEY_S3_TO_ELASTICSEARCH)) {
      flags.push(`${KEY_S3_TO_ELASTICSEARCH} should not be later than ${key}`)
    }
  }
  if (key === KEY_UPLOAD_CANDIDATES) {
    if (isLater(KEY_CREATE_CANDIDATES)) {
      flags.push(`${KEY_CREATE_CANDIDATES} should not be later than ${key}`)
    }
  }
  if ([
    KEY_CLEANUP_S3,
    KEY_CREATE_IMAGE_VARIANTS,
    KEY_SET_ALBUMPATH_COUNTS,
    KEY_CREATE_ALBUMS,
    KEY_CREATE_KEYWORDS,
    KEY_S3_TO_ELASTICSEARCH
  ].indexOf(key) !== -1) {
    if (isLater(KEY_UPLOAD_CANDIDATES)) {
      flags.push(`${KEY_UPLOAD_CANDIDATES} should not be later than ${key}`)
    }
  }
  if (key === KEY_S3_TO_ELASTICSEARCH) {
    if (isEarlier(KEY_GRAPH_UPLOAD)) {
      flags.push(`${KEY_GRAPH_UPLOAD} should not be earlier than ${key}`)
    }
    if (isLater(KEY_CREATE_KEYWORDS)) {
      flags.push(`${KEY_CREATE_KEYWORDS} should not be later than ${key}`)
    }
    if (isLater(KEY_CREATE_ALBUMS)) {
      flags.push(`${KEY_CREATE_ALBUMS} should not be later than ${key}`)
    }
  }
  if (key === 'KEY_GRAPH_BACKUP_UPDATED') {
    if (data[KEY_GRAPH_OLD_NODES] === 0 && isLater(KEY_GRAPH_UPDATED)) {
      flags.push(`${KEY_GRAPH_UPDATED} should not be later than ${key} when ${KEY_GRAPH_OLD_NODES} is zero`)
    }
  }
  if (key === KEY_GRAPH_UPLOAD) {
    if (isLater(KEY_CREATE_KEYWORDS)) {
      flags.push(`${KEY_CREATE_KEYWORDS} should not be later than ${key}`)
    }
  }
  if ([
    KEY_GRAPH_DISPLAY_UPDATED,
    KEY_GRAPH_DISCOVER_UPDATED,
    KEY_GRAPH_STRUCTURE_UPDATED
  ].indexOf(key) !== -1) {
    if (isLater(KEY_GRAPH_UPDATED)) {
      flags.push(`${KEY_GRAPH_UPDATED} should not be later than ${key}`)
    }
  }
  return flags
}

const mapStateToProps = ({ auth }) => ({
  fetchData: () => fetch(ENDPOINT_SCRIPTS_INFO, {
    token: auth.token
  })
})

const mapDispatchToProps = {
}

export default connect(mapStateToProps, mapDispatchToProps)(ScriptsInfo)



// WEBPACK FOOTER //
// ./src/pages/ScriptsInfo.js